import type { FC, ReactNode } from 'react'
import React, { useState, useEffect } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import type { SnackbarProps } from '@material-ui/core/Snackbar'
import Snackbar from '@material-ui/core/Snackbar'
import type { AlertProps } from '@material-ui/lab/Alert'
import MuiAlert from '@material-ui/lab/Alert'
import NotificationsNoneOutlined from '@material-ui/icons/NotificationsNoneOutlined'
import Close from '@material-ui/icons/Close'
import type { ITheme } from '@homeviews/styles'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      zIndex: 9999,
      position: 'absolute',
    },
    popup: {
      display: 'flex',
      padding: 15,
      paddingRight: 45,
      border: '1px solid rgba(0,0,0,0.04)',
      borderRadius: 5,
      backgroundColor: theme.colors.white,
      boxShadow: '0 8px 15px 0 rgba(46,81,123,0.15)',
      width: '100%',
      maxWidth: 377,
      '& h4': {
        marginBottom: '8px!important',
      },
    },
    iconWrapper: {
      color: '#F0B896',
      marginTop: 1,
      marginRight: 5,
    },
    closeIconWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      top: 20,
      right: 20,
      height: 20,
      width: 20,
      color: theme.colors.grey490,
      '&:hover': {
        cursor: 'pointer',
      },
    },
  }),
)
function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

interface IProps extends SnackbarProps {
  className?: string
  alertMessage?: string
  onClose: any
  status?: 'success' | 'error' | 'warning' | 'info'
  content?: ReactNode
  customLayout?: boolean
  open?: boolean
  closeTime?: number
}

const CustomSnackbar: FC<IProps> = ({
  className,
  alertMessage,
  onClose,
  status = 'success',
  content,
  open,
  closeTime = 3000,
  ...rest
}) => {
  const classes = useStyles()

  const [popupOpen, setPopupOpen] = useState(false)

  useEffect(() => {
    setPopupOpen(open)
    let hideTimeout = null

    if (hideTimeout) {
      clearTimeout(hideTimeout)
    }

    hideTimeout = setTimeout(() => {
      setPopupOpen(false)
    }, closeTime)

    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout)
      }
    }
  }, [open])

  function handleCloseClick() {
    setPopupOpen(false)
  }

  function handleClose() {
    setPopupOpen(false)
    // Pass null as Event and 'close reason', required by parent components
    // todo refactored this
    onClose(null, null)
  }

  return (
    <div className={clsx(classes.root, className && className)}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...rest}
        open={popupOpen}
        TransitionProps={{
          onExited: () => {
            handleClose()
          },
        }}
      >
        {status === 'info' ? (
          <div className={classes.popup}>
            <div className={classes.iconWrapper}>
              <NotificationsNoneOutlined />
            </div>
            <div>{content || alertMessage || ''}</div>
            <div
              className={classes.closeIconWrapper}
              onClick={handleCloseClick}
            >
              <Close />
            </div>
          </div>
        ) : (
          <Alert severity={status} onClose={handleClose}>
            {alertMessage}
          </Alert>
        )}
      </Snackbar>
    </div>
  )
}

export default CustomSnackbar
